import { useTranslation } from "next-i18next";
import parse from "html-react-parser";
import Link from "next/link";
import Accordion from "../../components/v2/accordion/accordion";
import ProductHeroSection from "../../components/v2/common/product-hero";
import MwContactUs from "../../components/v2/contact-us";
import ProductResources from "../../components/v2/product/product-resource";
import "../../styles/scss/_browser-page.module.scss";
// `.single-integration` (incl. its `.browser-testing` variant) is defined here;
// without it the section below renders unstyled with full-size browser logos.
import "../../styles/scss/partner.module.scss";
import '../../styles/scss/_faq-section.module.scss';
import ProductOverviewSection from "../../components/v2/product-overview-section";
import FeaturesMonitoringV2 from "../../components/v2/tabs/features-monitoring-v2";
import CallToActionDocsLink from "../../components/v2/blog/call-to-action-docs-link";
import ClientTabsTestimonials from "../../components/v2/mw-carousel/testimonial-with-tabs";
import { SyntheticLogo, RUM, APM } from "../../components/v2/icons-v2";

export default function BrowserTesting() {
    const { t } = useTranslation("common");
    const featuresData = t('product.browser-testing.features.detailedFeatures', { returnObjects: true }) as any[]
    const features = Array.isArray(featuresData) ? featuresData : []

    const faqsData = t('product.browser-testing.faq.questions', { returnObjects: true }) as any[]
    const faqs = Array.isArray(faqsData) ? faqsData : []

    const resourceData = t('product.browser-testing.resources', { returnObjects: true }) as any[]
    const resource = Array.isArray(resourceData) ? resourceData : []

    const relatedProductsData = t('product.browser-testing.relatedProducts', { returnObjects: true }) as any

    const integrationData = t('product.browser-testing.integration', { returnObjects: true }) as any

    const productOverviewData = {
                title: "Product overview",
                buttons: [
                    {
                        label: "View Docs",
                        link: "https://docs.middleware.io/workflow/synthetic-monitoring/browser-tests-monitoring",
                        className: "ahref-btn primary",
                    },
                    {
                        label: "View Pricing",
                        link: "/pricing/",
                        className: "ahref-btn secondary",
                    },
                ],
                description:
                    "Middleware Browser Testing lets you simulate real user interactions across browsers and devices to monitor web app performance and UX. It provides session replays, core web vitals, step screenshots, error insights, cross-browser checks, and alerts to detect and fix frontend issues before users are affected. ",
                worksWellWithTitle: "Works Well With",
                worksWellWith: [
                    {
                        title: "Synthetic",
                        link: "/product/synthetic-monitoring/",
                        icon: <SyntheticLogo />
                    },
                    {
                        title: "APM",
                        link: "/product/apm/",
                        icon: <APM />
                    },
                    {
                        title: "RUM",
                        link: "/product/real-user-monitoring/",
                        icon: <RUM />
                    },
                ],
            };

    return (
        <>
            <ProductHeroSection title={<h1><span className={"text-gradient"}>{t('product.browser-testing.hero.title')} <br/> </span> {t('product.browser-testing.hero.titleHighlight')} </h1>}
                                description={t('product.browser-testing.hero.description')}
                                img={"/images/v2/products/browser-testing.webp"} imgMobile={"/images/v2/products/browser-testing-mobile.webp"} imgAlt={t('product.browser-testing.hero.imageAlt')} preloadImg hasActions={true} className={"browser-testing"} tips={t('product.hero.tips')}/>

            <ProductOverviewSection productOverviewData={productOverviewData} />

            <FeaturesMonitoringV2 productFeatures={features} tourId={"browser-tour"} />

            <CallToActionDocsLink
                title={'Try it free and improve web experience for all users'}
                docsLink="https://docs.middleware.io/workflow/synthetic-monitoring/browser-tests-monitoring"
            />

            <section className="testimonial-section">
                <div className="container">
                    <div className="section-tag testimonial">{t("testimonials.tag")} 👤</div>
                    <h2>Hear it from the best: Why <span className="text-gradient">top companies</span> trust Middleware</h2>

                    <ClientTabsTestimonials />
                </div>
            </section>

            <div className={"single-integration browser-testing"}>
                <div className={"container"}>
                    <div className={"d-flex"}>
                        <div className={"left-block"}>
                            <div className={'section-tag'}>{integrationData.sectionTag}</div>
                            <h2>{integrationData.title} <span className={"text-gradient"}> {integrationData.titleHighlight} <br/> </span> {integrationData.titleHighlightLineBreak}</h2>
                        </div>
                        <div className={"right-block"}>
                            {integrationData.browsers.map((browser: any, index: number) => (
                                <div key={index} className={"llm"}>
                                    <div>
                                        <div className={"d-flex"}>
                                            <img src={index === 0 ? "/images/product/chrome.svg" : "/images/product/edge-browser.svg"} alt={`${browser.name} ${integrationData.iconSuffix}`} width={52} height={52}/>
                                            <h4>{browser.name}</h4>
                                        </div>
                                        <Link href={browser.docsLink} target={"_blank"}><p className={"read-more"}>{integrationData.docsLabel}</p></Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <MwContactUs/>
                </div>
            </div>

            <ProductResources resources={resource} title={t('product.shared.resources.title')}/>

            <section className={"faq-section"}>
                <div className={"container"}>
                    <h2>{t('product.browser-testing.faq.title')}</h2>
                    <p>{t('product.browser-testing.faq.subtitle')}</p>
                    <div className={"faq-item"}>
                        <Accordion faqs={faqs.map(faq => ({
                            ...faq,
                            answerPlain: faq.text,
                            text:
                                faq.textHtml != null && faq.textHtml !== ""
                                    ? parse(faq.textHtml)
                                    : <p>{faq.text}</p>
                        }))} showGroups={true}/>
                    </div>
                </div>
            </section>
        </>
    )
}